<script setup>
import {ref, computed, watch, reactive, onMounted} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import markdownit from 'markdown-it'
import {PlusIcon, UsersIcon, ClockIcon, MagnifyingGlassIcon, UserCircleIcon, FireIcon, CheckIcon} from '@heroicons/vue/24/solid'

import Header from './Header.vue'
import Tooltip from './Tooltip.vue'
import ButtonLoad from './ButtonLoad.vue'
import UserName from './UserName.vue'
import MarkdownRenderer from './MarkdownRenderer.vue'

import {useMessageStore } from '@/stores/messagestore.js'
const messageStore = useMessageStore();

import {useUserStore } from '@/stores/userstore.js'
const userStore = useUserStore();

const app_server = import.meta.env.VITE_APP_SERVER;
const router = useRouter();
var slug;

const props = defineProps({
});

const extra_data = reactive({
    clubs: [],
    view_create_form: false,
    offset: 0,
    total: 0,
    search: "",
});


const club_data = reactive({
    name: "",
    tag: "",
    description: "",
})

onMounted(async () => {
    get_clubs();
});

async function get_clubs(){
    var url = app_server + `/club/`;

    const get_parameters = {
        offset: extra_data.offset,
        search: extra_data.search,
    };
    const search_params = new URLSearchParams(get_parameters)
    url += "?" + search_params.toString()

    const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers:{
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("jwt"),
        },
    });
    
    const data = await response.json();

    if(data.status == "success"){
        extra_data.clubs.length = 0;
        extra_data.clubs.push(...data.clubs);
        extra_data.total = data.total || 0;
    }
    
    console.log(data);
}

async function create_club(){
    if(!confirm("Are you sure you want to create a club?\n\nWe remind you that you as the club leader are responsible for adhering to the ToS and that clubs that don't adhere to the ToS will be banned.")){
        return;
    }
    const data = Object.assign({}, club_data);
    var url = app_server + `/club/create/`;

    const tag_regex = /^[0-9a-z]{3}$/i
    if(data["tag"].length != 3 || data["tag"].match(tag_regex) == null){
        messageStore.alertUser("Tag incorrect", 
            "Club tags can only be 3 alphanumeric characters long");
        return;
    }

    const response = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers:{
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("jwt"),
        },
        body: JSON.stringify(data),
    });
    
    const resp_data = await response.json();

    if(resp_data.status == "error"){
        messageStore.alertUser("Error", resp_data.message);
    }
    else if(resp_data.status == "success"){
        messageStore.alertUser("Success", "Your club has been created!");
        router.push({name: "club-mod", params: {club_id: resp_data.club.club_id}});
    }

    console.log(resp_data);
}

const page_size = 20;
async function next(delta){
    var new_offset = extra_data.offset + delta;
    const total = extra_data.total || 0;

    new_offset = Math.min(new_offset, total - page_size);
    new_offset = Math.max(0, new_offset);

    extra_data.offset = new_offset;

    await get_clubs();
}

</script>

<template>
    <Header />
    <div class="container mx-auto mb-64 flex flex-col items-center gap-y-8">
    <h1 class="text-4xl font-semibold text-center mt-8 mb-12 tracking-wide flex items-center justify-center gap-x-4">
        Clubs
        <Tooltip text="Create a new club">
            <PlusIcon class="size-8 hover:text-accent-3-color cursor-pointer" 
                      @click="extra_data.view_create_form = true"
            />
        </Tooltip>
    </h1>
    <div class="flex items-center gap-x-1">
        <input 
            type="text" class="h-10" v-model="extra_data.search"
        />
        <ButtonLoad
            :fn="get_clubs"
            class="cursor-pointer size-8"
        >
            <MagnifyingGlassIcon 
                class="size-8 hover:text-accent-3-color" 
            />
        </ButtonLoad>
    </div>
        <div 
            v-if="extra_data.view_create_form"
            class="flex flex-col gap-y-3 my-8 p-6 rounded-lg box bg-main-2-color max-w-prose mx-auto"
        >
            <div>
                <label>Name</label>
                <input type="text"
                       class="text-xl w-full" 
                       maxlength=80
                       placeholder="Your club name"
                       v-model="club_data.name" 
                />
            </div>
            <div>
                <label>Tag</label>
                <input type="text"
                       class="text-xl w-full" 
                       maxlength=3
                       minlength=3
                       required
                       pattern="[A-Z0-9]{3}"
                       placeholder="Your club tag"
                       v-model="club_data.tag" 
                />
                <span class="font-light">
                    The club tag must consist of 3 alphanumeric characters (case-insensitive).
                </span>
            </div>
            <div>
                <label>Description</label>
                <textarea  data-lesson class="w-full" 
                    v-model="club_data.description" 
                    placeholder="Describe the club." 
                />
            </div>
                <ButtonLoad class="btn btn-blue h-12 my-4 w-full"
                    :fn="create_club"
                    :disabled="!club_data.description || !club_data.name || !club_data.tag"
                >
                    Create Club
                </ButtonLoad>
                <button class="btn btn-danger"
                    @click="extra_data.view_create_form = false"
                >
                    Cancel
                </button>
        </div>
    <div class="grid grid-cols-1 md:grid-cols-2 w-full gap-8">
        <div class="flex box bg-main-2-color shadow rounded py-4 px-6 group relative transition-all"
            v-for="club in extra_data.clubs"
            :class="{'!bg-accent-1-color': club.start_time*1000 > Date.now()}"
        >
            <div class="grow flex flex-col gap-y-2 w-full">
                <h2 class="text-xl text-center tracking-wide font-medium 
                    mb-4 group-hover:text-accent-3-color w-full truncate"
                >
                    <router-link :to="{name: 'club-info', params:{club_id: club.club_id}}">
                        <div class="flex flex-col gap-y-4">
                            <span class="w-full px-8 truncate">
                                {{ club.name }}
                            </span>
                            <span class="w-full text-3xl px-8 truncate">
                                {{ club.tag }}
                            </span>
                        </div>
                    </router-link>
                </h2>

                <div class="flex gap-x-1 items-center">
                    <Tooltip text="Club Director">
                        <UserCircleIcon class="w-4 h-4" />
                    </Tooltip>
                    <UserName :user_id="club.creator" />
                </div>
                <div class="flex gap-x-1 items-center">
                    <Tooltip text="Since">
                        <ClockIcon class="size-4" />
                    </Tooltip>
                    <span>{{ new Date(club.create_time*1000).toLocaleDateString() }}</span>
                </div>
                <div class="flex gap-x-1 items-center">
                    <Tooltip text="Rating">
                        <FireIcon class="size-4" />
                    </Tooltip>
                    <span>{{ club.rating.toFixed(0) }}</span>
                </div>
                <div class="flex gap-x-1 items-center">
                    <Tooltip text="Experience">
                        XP
                    </Tooltip>
                    <span>{{ club.experience.toFixed(0) }}</span>
                </div>
                <div class="flex gap-x-1 items-center">
                    <Tooltip text="Members">
                        <UsersIcon class="size-4" />
                    </Tooltip>
                    <span>{{ club.members.length }}</span>
                </div>
                <div class="mt-4 text-lg font-thin overflow-y-hidden h-24 relative">
                    <!-- <div class="absolute bottom-0 w-full h-10 bg-gradient-to-t from-main-2-color" /> -->
                    <MarkdownRenderer 
                        :markdown="club.description" 
                        :save="false" 
                    />
                </div>
                <router-link :to="{name: 'club-info', params:{club_id: club.club_id}}">
                    <button class="btn btn-blue h-10 w-full">Info</button>
                </router-link>
            </div>
        </div>
    </div>
    <div class="flex justify-between w-full px-2 mb-8">
        <ButtonLoad 
            class="btn btn-blue"
            :fn="() => next(-page_size)"
            :disabled="extra_data.offset <= 0"
        >
            Previous
        </ButtonLoad>
        <span>{{ extra_data.total }} Clubs</span>
        <ButtonLoad 
            class="btn btn-blue"
            :fn="() => next(page_size)"
            :disabled="extra_data.offset + page_size >= extra_data.total"
        >
            Next
        </ButtonLoad>
    </div>
    </div>
</template>

<style scoped>
</style>


